import React, { useEffect, useMemo, useState } from "react";
import { useEvent } from "../../../context/EventContext";
import { useAuth } from "../../../context/AuthContext";
import ProfilePage from "./Registration/ProfilePage";
import RegistrationTable from "./Registration/RegistrationTable";

// * The Registration component renders the tables and filters for viewing and managing registration data.
//  * It renders the RegistrationTable component if the user has not clicked on a user's profile picture.
//  * When the user clicks on a user's profile picture, it renders the ProfilePage component.
//  * The component also filters and sorts the data based on the registration type and job status.
const Registration = () => {
  const {
    selectedEvent,
    selectedUser,
    setSelectedUser,
    fetchConnectionStatus,
  } = useEvent();
  const { user } = useAuth();
  const [registeredType, setRegisteredTypes] = useState(null);
  const [showMessage, setShowMessage] = useState(false); // To track if the image is clicked

  // Memoize registrationDetailsArray and registrationData to avoid re-calculation on every render
  const registrationDetailsArray = useMemo(() => {
    return selectedEvent && selectedEvent[3] ? selectedEvent[3] : [];
  }, [selectedEvent]);

  const registrationData = useMemo(() => {
    return selectedEvent && selectedEvent[6] ? selectedEvent[6] : [];
  }, [selectedEvent]);

  // Filter registration data based on registration type and job status
  const [filterRegistrationType, setFilterRegistrationType] = useState(""); // For filtering by registration type
  const [sortJobStatus, setSortJobStatus] = useState(""); // For sorting by job status
  const [filteredData, setFilteredData] = useState([]); // For holding the final filtered and sorted data

  // Filter and sort the data whenever filter or sort criteria changes
  useEffect(() => {
    let updatedData = registrationData;
    if (filterRegistrationType) {
      updatedData = updatedData.filter(
        (registration) =>
          registration.registrationTypeID?._id === filterRegistrationType
      );
    }
    if (sortJobStatus) {
      updatedData = updatedData.filter(
        (registration) => registration.userID?.jobStatus === sortJobStatus
      );
    }
    setFilteredData(updatedData);
  }, [filterRegistrationType, sortJobStatus, registrationData]);

  // Clear all filters and sorting
  const clearFiltersAndSorting = () => {
    setFilterRegistrationType("");
    setSortJobStatus("");
    setFilteredData(registrationData); // Reset to show all data
  };

  // Check which registration types the user has already registered for
  useEffect(() => {
    const userRegistration = registrationData.find(
      (data) => data?.userID?._id === user.id
    );
    if (userRegistration) {
      setRegisteredTypes(userRegistration.registrationTypeID?._id);
    }
  }, [registrationData, user.id]);

  // Handle image click to show the user data
  const handleImageClick = async (userData) => {
    setSelectedUser(userData); // Set the clicked user's data
    setShowMessage(true); // Show the user data div
    // Fetch connection status from the backend
    await fetchConnectionStatus(userData._id); // Call to get status
  };

  return (
    <div className="registration-section">
      {/* Conditionally show either the tables and filters or the "Hi" message */}
      {showMessage && selectedUser ? (
        <ProfilePage
          setShowMessage={setShowMessage}
          selectedUser={selectedUser}
        />
      ) : (
        <RegistrationTable
          filterRegistrationType={filterRegistrationType}
          setFilterRegistrationType={setFilterRegistrationType}
          sortJobStatus={sortJobStatus}
          setSortJobStatus={setSortJobStatus}
          clearFiltersAndSorting={clearFiltersAndSorting}
          filteredData={filteredData}
          handleImageClick={handleImageClick}
          registrationDetailsArray={registrationDetailsArray}
          registrationData={registrationData}
          registeredType={registeredType}
        />
      )}
    </div>
  );
};

export default Registration;
